import { trackEvent } from '@patrianna/analytics'
import { BottomNavigationAction, Drawer } from '@patrianna/core-components'
import { useDialogVisibilityByName } from '@patrianna/dialog-manager'
import { useMediaQuery, useScrollLock } from '@patrianna/shared-hooks'
import { nextTick } from '@patrianna/shared-utils'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { Facebook, LoginVariant, Menu } from '@patrianna-icons/material-icons'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect } from 'react'
import type { SyntheticEvent } from 'react'

import { connectFacebook } from 'analyticActions/facebook/actions'
import Button from 'components/Button'
import { IS_BRAND_PULSZ, IS_BRAND_PULSZBINGO } from 'config/brands'
import ROUTES from 'config/routes.json'
import DarkThemeSwitch from 'containers/DarkThemeSwitcher'
import LogoLink from 'containers/LogoLink'
import MenuLink from 'containers/Menu/MenuLink'
import { useRouterPushWithCallback } from 'containers/NavigationProvider'
import { useBuilderData } from 'hooks/useBuilderCMSData'
import useIsLandscape from 'hooks/useIsLandscape'
import useMenuVisibility from 'hooks/useMenuVisibility'
import content from 'prismic-translates/common.json'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { logOut } from 'store/modules/auth/actions'
import { facebookConnectedSelector, isLoggedInSelector } from 'store/modules/user/selectors'
import mobileLinks from 'temp/mobileLinks.json'
import { INTERNAL_GAME_NAVIGATION, setDataToSessionStorage } from 'utils/sessionStorage'

import AccountInfo from '../../../AccountInfo'
import LoyaltyStatus from '../../../components/LoyaltyStatus'
import MenuFooter from '../../../MenuFooter'
import getLinks from '../../../utils'

import classes from './styles.module.scss'

const t = getTranslationFromJson([content])

type Props = {
  isMobile?: boolean
}

function More(props: Props) {
  // selectors
  const isLoggedIn = useAppSelector(isLoggedInSelector)
  const isMobileMedia = useMediaQuery('xs')
  const isMobile = props.isMobile || isMobileMedia
  const isLandscape = useIsLandscape()
  const facebookConnected = useAppSelector(facebookConnectedSelector)
  const isExtraGoldDialogVisible = useDialogVisibilityByName('EXTRA_GOLD_DIALOG')
  const isShopDialogVisible = useDialogVisibilityByName('SHOP_DIALOG')
  const dispatch = useAppDispatch()

  const links = getLinks(mobileLinks)
  const routerPush = useRouterPushWithCallback()
  const query = useSearchParams()
  const pathname = usePathname()
  const { isMenuOpened, toggleMenu } = useMenuVisibility()
  const { builderData: promoCategories } = useBuilderData(process.env.MODEL_PROMO_CATEGORIES)
  const promoCategoriesPaths = promoCategories.map((item) => item.data.pathname)

  useScrollLock(isMenuOpened)

  const hideMenu = useCallback(() => {
    toggleMenu(false)
  }, [toggleMenu])

  const onOpenMenu = (event: SyntheticEvent<any, Event> | KeyboardEvent) => {
    nextTick().then(() => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as KeyboardEvent).key === 'Tab' || (event as KeyboardEvent).key === 'Shift')
      ) {
        return
      }

      toggleMenu(true)
    })
  }

  const handleClickLogoLink = () => {
    nextTick().then(() => {
      hideMenu()
      routerPush(ROUTES.HOME)
      handleTrackEvent('logo')
    })
  }

  const handleClickJoinLink = () => {
    nextTick().then(() => {
      hideMenu()
      handleTrackEvent('join_now')
      routerPush(ROUTES.REGISTER)
    })
  }

  const handleClickMenuLink = (name: string, onClick?: () => void) => {
    nextTick().then(() => {
      hideMenu()
      onClick?.()
      handleTrackEvent(name)
    })
  }

  const handleClickFacebookLink = () => {
    nextTick().then(() => {
      dispatch(connectFacebook(routerPush, query, pathname))
      handleTrackEvent('connect_facebook')
    })
  }

  const handleClickLogoutLink = () => {
    nextTick().then(() => {
      hideMenu()
      dispatch(logOut())
      handleTrackEvent('logout')
    })
  }

  const handleTrackEvent = (targetName: string) => {
    trackEvent(`main_menu_${targetName}_button_click`)
  }

  useEffect(() => {
    if (isExtraGoldDialogVisible || (isMobile && isShopDialogVisible)) {
      hideMenu()
    }
  }, [isMobile, isExtraGoldDialogVisible, isShopDialogVisible, hideMenu])

  return (
    <>
      <BottomNavigationAction
        label='More'
        icon={<Menu />}
        onClick={onOpenMenu}
        className={classes.navigationButton}
        data-testid='bottom-navigation-menu'
      />

      <Drawer className={classes.root} open={isMenuOpened} onClose={hideMenu} disableEnforceFocus>
        <div className={classes.menuItems}>
          <div className={classes.topContent}>
            <div className={classes.logoLink} onClick={handleClickLogoLink}>
              <LogoLink place='more' />
            </div>

            {isLoggedIn ? (
              <AccountInfo handleTrackEvent={handleTrackEvent} hideMenuCallback={hideMenu} />
            ) : (
              <Button onClick={handleClickJoinLink} size='small' className={classes.button}>
                {t('join_now')}
              </Button>
            )}
          </div>

          {IS_BRAND_PULSZBINGO && <hr className={classes.divider} />}

          <div className={classes.links}>
            {IS_BRAND_PULSZ && isLoggedIn && <LoyaltyStatus hideMenuCallback={hideMenu} />}

            {links.map((i) => (
              <MenuLink
                promoCategoriesPaths={promoCategoriesPaths}
                {...i}
                key={i.id}
                onClick={() => {
                  handleClickMenuLink(i.name, i?.onClick)
                  if (i.name.toLowerCase().trim() === 'bingo') {
                    setDataToSessionStorage(INTERNAL_GAME_NAVIGATION, 'true')
                  }
                }}
              />
            ))}

            {isLoggedIn && !facebookConnected && (
              <MenuLink
                promoCategoriesPaths={promoCategoriesPaths}
                text='Connect Facebook'
                icon={Facebook}
                onClick={handleClickFacebookLink}
              />
            )}

            <DarkThemeSwitch handleTrackEvent={handleTrackEvent} />

            {isLoggedIn && (
              <MenuLink
                promoCategoriesPaths={promoCategoriesPaths}
                text='Logout'
                icon={LoginVariant}
                onClick={handleClickLogoutLink}
              />
            )}
            {isLandscape && <MenuFooter handleTrackEvent={handleTrackEvent} />}
          </div>
          {!isLandscape && <MenuFooter handleTrackEvent={handleTrackEvent} />}
        </div>
      </Drawer>
    </>
  )
}

export default More
